import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { deriveAccessibleBrandColor } from '@marva/shared/email-composer/style-resolver'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { governanceApi, workspacesApi, type EmailBrandDetectionReview, type EmailBrandFactProvenance, type EmailBrandFacts, type EmailBrandSuggestion, type EmailPreviewCart } from '../../services/api'
import { DEFAULT_FACTS } from './emailBrandFacts'
import EmailPreview from './EmailPreview'
import ChatPreview, { PreviewSurfaceControl, type PreviewSurface } from './ChatPreview'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Chip,
  ColorField,
  Eyebrow,
  FieldLabel,
  FileField,
  Hint,
  Inline,
  KeyValue,
  LoadingState,
  Section,
  SectionLayout,
  SegmentedControl,
  Stack,
} from '../../ds'

interface EmailStylesSectionProps {
  onClose?: () => void
  /**
   * The Brand tab's section nav (MAR-384). Given one, this section lays itself out as
   * nav · facts · preview aside — the preview needs the facts on screen, so the aside is
   * this component's to draw, not the tab's.
   */
  nav?: ReactNode
  /**
   * Where Shopify detection landed, and a confirmation moving it to `current` — the tab's
   * "To confirm" / "Up to date" hint. ⚠️ Detection still runs only when THIS section opens.
   */
  onDetection?: (state: EmailBrandDetectionReview['state']) => void
}
type BrandFacts = EmailBrandFacts
interface RasterLogo { data: string; pixelWidth: number; pixelHeight: 80 }
const MAX_SHOPIFY_LOGO_BYTES = 5 * 1024 * 1024
const SHOPIFY_LOGO_TIMEOUT_MS = 5000
const detectionReasonCopy = (reason?: string) => ({
  invalid_shop: 'Marva could not find the connected Shopify shop. Your current styling is unchanged.',
  http_error: 'Shopify Brand is unavailable right now. Your current styling is unchanged.',
  graphql_error: 'Shopify Brand is unavailable for this store. Your current styling is unchanged.',
  online_store_locked: 'Shopify Brand is unavailable while the Online Store channel is locked. Your current styling is unchanged.',
  network_error: 'Marva could not reach Shopify. Your current styling is unchanged.',
  timeout: 'Shopify took too long to respond. Your current styling is unchanged.',
  response_too_large: 'Shopify returned more brand information than Marva can safely read. Your current styling is unchanged.',
  invalid_response: 'Shopify returned brand information Marva could not read. Your current styling is unchanged.',
  refresh_conflict: 'The Shopify brand changed while this page was loading. Reopen brand styling to review the latest version.',
}[reason || ''] || 'Shopify brand detection is unavailable. Your current styling is unchanged.')
const FACT_KEYS = ['logo', 'brandColor', 'typeface', 'corners', 'paper'] as const
const FACT_LABELS: Record<typeof FACT_KEYS[number], string> = { logo: 'Logo', brandColor: 'Brand colour', typeface: 'Typeface', corners: 'Corners', paper: 'Paper' }
// "You set this" for `chosen` (v6, MAR-384): the merchant's own word, not a system tag.
const TAG_LABELS = { shopify: 'From Shopify', adjusted: 'Adjusted', default: 'Default', chosen: 'You set this' } as const
const LOCKED: [string, ReactNode][] = [
  ['Module order and one button', 'Marva keeps the message focused on one remembered reason and one action.'],
  ['Note length and tone', <>Those belong to <Link to="/authority?tab=voice&section=sound">How we sound</Link>, not styling.</>],
  ['Why line and reply door', 'They preserve provenance and a way back to the shop.'],
  ['Footer, address and unsubscribe', <>Commercial-email compliance is not a visual option. The address is set on <Link to="/authority?tab=store">Store</Link>.</>],
  // ⚠️ Not the design's "In chat, light or dark follows your site": once styling is set, chat
  // paints this paper on light and dark sites alike (`ChatPreview`, MAR-391) — only Marva's own
  // look, before any styling, follows the site. The clause would be true of the wrong case.
  ['Dark backgrounds', 'Mail clients invert them inconsistently, so paper stays warm or white.'],
]

function luminance(red: number, green: number, blue: number) {
  const linear = [red, green, blue].map(value => { const channel = value / 255; return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4 })
  return linear[0] * 0.2126 + linear[1] * 0.7152 + linear[2] * 0.0722
}

async function rasteriseBitmap(bitmap: ImageBitmap, paper: BrandFacts['paper'], inspect = false): Promise<RasterLogo> {
  const pixelHeight = 80 as const
  const pixelWidth = Math.max(1, Math.min(1200, Math.round(bitmap.width * pixelHeight / bitmap.height)))
  const canvas = document.createElement('canvas'); canvas.width = pixelWidth; canvas.height = pixelHeight
  const context = canvas.getContext('2d', { willReadFrequently: inspect })
  if (!context) throw new Error('This browser could not inspect the logo.')
  context.drawImage(bitmap, 0, 0, pixelWidth, pixelHeight)
  if (inspect) {
    const pixels = context.getImageData(0, 0, pixelWidth, pixelHeight).data
    const paperRgb = paper === 'warm' ? [250, 247, 242] : [255, 255, 255]
    const paperLum = luminance(paperRgb[0], paperRgb[1], paperRgb[2])
    let visible = 0; let totalContrast = 0
    for (let index = 0; index < pixels.length; index += 4) {
      const alpha = pixels[index + 3] / 255
      if (alpha < 0.05) continue
      visible += 1
      const logoLum = luminance(pixels[index] * alpha + paperRgb[0] * (1 - alpha), pixels[index + 1] * alpha + paperRgb[1] * (1 - alpha), pixels[index + 2] * alpha + paperRgb[2] * (1 - alpha))
      totalContrast += (Math.max(paperLum, logoLum) + 0.05) / (Math.min(paperLum, logoLum) + 0.05)
    }
    if (visible === 0 || totalContrast / visible < 2) throw new Error('The Shopify logo is too pale to read on email paper.')
  }
  return { data: canvas.toDataURL('image/png'), pixelWidth, pixelHeight }
}

async function rasteriseLogo(file: File): Promise<RasterLogo> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw new Error('Choose a PNG, JPEG or WebP image. SVG logos are not supported by Gmail and Outlook.')
  if (file.size > 5 * 1024 * 1024) throw new Error('Logo must be smaller than 5 MB.')
  const bitmap = await createImageBitmap(file)
  try { return await rasteriseBitmap(bitmap, 'white') } finally { bitmap.close() }
}

async function boundedImageBlob(response: Response) {
  const contentType = response.headers?.get('content-type') || ''
  if (contentType && !/^image\/(png|jpeg|webp)(?:;|$)/i.test(contentType)) throw new Error('The Shopify logo was not a supported image.')
  const declared = Number(response.headers?.get('content-length'))
  if (Number.isFinite(declared) && declared > MAX_SHOPIFY_LOGO_BYTES) throw new Error('The Shopify logo is larger than 5 MB.')
  const reader = response.body?.getReader()
  if (!reader) {
    const blob = await response.blob()
    if (blob.size > MAX_SHOPIFY_LOGO_BYTES) throw new Error('The Shopify logo is larger than 5 MB.')
    return blob
  }
  const chunks: BlobPart[] = []; let size = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    size += value.byteLength
    if (size > MAX_SHOPIFY_LOGO_BYTES) { await reader.cancel(); throw new Error('The Shopify logo is larger than 5 MB.') }
    chunks.push(value.slice().buffer as ArrayBuffer)
  }
  return new Blob(chunks, { type: contentType || 'image/png' })
}

async function inspectShopifyLogo(url: string, paper: BrandFacts['paper'], outerSignal: AbortSignal) {
  const controller = new AbortController(); const abort = () => controller.abort()
  if (outerSignal.aborted) abort(); else outerSignal.addEventListener('abort', abort, { once: true })
  const timeout = window.setTimeout(abort, SHOPIFY_LOGO_TIMEOUT_MS)
  try {
    const response = await fetch(url, { signal: controller.signal, credentials: 'omit', redirect: 'error' })
    if (!response.ok) throw new Error('The Shopify logo could not be read safely by this browser.')
    const bitmap = await createImageBitmap(await boundedImageBlob(response))
    try { return await rasteriseBitmap(bitmap, paper, true) } finally { bitmap.close() }
  } finally {
    window.clearTimeout(timeout); outerSignal.removeEventListener('abort', abort)
  }
}

/** One of 2–3 values, as a radiogroup (D-18). Labels are the values, capitalised. */
const Seg = <T extends string>({ value, values, onChange, disabled = false, label }: { value: T; values: readonly T[]; onChange: (v: T) => void; disabled?: boolean; label: string }) => (
  <SegmentedControl
    label={label}
    disabled={disabled}
    value={value}
    options={values.map(v => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))}
    onChange={v => onChange(v as T)}
  />
)

const EmailStylesSection = ({ onClose, nav, onDetection }: EmailStylesSectionProps) => {
  const onDetectionRef = useRef(onDetection); useLayoutEffect(() => { onDetectionRef.current = onDetection }, [onDetection])
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore(); const { user } = useUser()
  const [facts, setFacts] = useState<BrandFacts>(DEFAULT_FACTS); const [savedFacts, setSavedFacts] = useState<BrandFacts>(DEFAULT_FACTS)
  const [suggestion, setSuggestion] = useState<EmailBrandSuggestion | null>(null); const [detectionReason, setDetectionReason] = useState<string | null>(null)
  const [provenanceOverrides, setProvenanceOverrides] = useState<Partial<Record<keyof BrandFacts, EmailBrandFactProvenance>>>({})
  const [chosenFacts, setChosenFacts] = useState<Set<keyof BrandFacts>>(new Set()); const [useShopifyName, setUseShopifyName] = useState(false)
  const [cart, setCart] = useState<EmailPreviewCart | null>(null); const [loading, setLoading] = useState(true); const [workspaceLoaded, setWorkspaceLoaded] = useState(false)
  const [saving, setSaving] = useState(false); const [uploading, setUploading] = useState(false); const [sending, setSending] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null); const [previewOffer, setPreviewOffer] = useState(false); const [message, setMessage] = useState<string | null>(null)
  /** Whether any styling is stored: with none, the widget keeps its own look until a draft or save. */
  const [hasStoredStyles, setHasStoredStyles] = useState(false); const [surface, setSurface] = useState<PreviewSurface>('email')
  const workspaceIdRef = useRef(currentWorkspace?.id); useLayoutEffect(() => { workspaceIdRef.current = currentWorkspace?.id }, [currentWorkspace?.id])

  useEffect(() => {
    const workspaceId = currentWorkspace?.id; const logoController = new AbortController(); let active = true
    setLoading(true); setWorkspaceLoaded(false); setMessage(null); setCart(null); setPreviewHtml(null); setSaving(false); setUploading(false); setSending(false)
    setSuggestion(null); setDetectionReason(null); setProvenanceOverrides({}); setChosenFacts(new Set()); setUseShopifyName(false); setFacts(DEFAULT_FACTS); setSavedFacts(DEFAULT_FACTS); setHasStoredStyles(false)
    if (!workspaceId) { setLoading(false); return () => { active = false; logoController.abort() } }
    Promise.all([workspacesApi.get(workspaceId), workspacesApi.getEmailPreviewCart(workspaceId), workspacesApi.refreshEmailBrandDetection(workspaceId)])
      .then(async ([workspace, preview, detection]) => {
        if (!active) return
        if (!workspace.success || !workspace.data) throw new Error(workspace.error || 'Could not load current styling.')
        const stored = (workspace.data as { emailStyles?: BrandFacts })?.emailStyles; const initial = stored ? { ...DEFAULT_FACTS, ...stored } : DEFAULT_FACTS
        setFacts(initial); setSavedFacts(initial); setHasStoredStyles(Boolean(stored)); setWorkspaceLoaded(true); if (preview.success && preview.data) setCart(preview.data)
        if (!detection?.success || !detection.data) { setDetectionReason(detection?.error || 'Shopify brand detection is unavailable. Your current styling is unchanged.'); return }
        onDetectionRef.current?.(detection.data.state)
        if (detection.data.state === 'unavailable') { setDetectionReason(detectionReasonCopy(detection.data.reason)); return }
        const proposed = detection.data.state === 'proposed' ? detection.data.suggestion : undefined
        if (!proposed) return
        setSuggestion(proposed); setFacts({ ...proposed.facts, logo: proposed.logoCandidate ? null : initial.logo })
        if (!proposed.logoCandidate) return
        setUploading(true)
        try {
          const raster = await inspectShopifyLogo(proposed.logoCandidate.url, proposed.facts.paper, logoController.signal)
          if (!active || workspaceIdRef.current !== workspaceId) return
          const upload = await workspacesApi.uploadEmailLogo(workspaceId, { pngBase64: raster.data.split(',')[1], pixelWidth: raster.pixelWidth, pixelHeight: 80 })
          if (!active || workspaceIdRef.current !== workspaceId) return
          const logo = (upload.data as { logo?: BrandFacts['logo'] } | undefined)?.logo
          if (!upload.success || !logo) throw new Error(upload.error || 'The Shopify logo could not be prepared.')
          setFacts(value => ({ ...value, logo }))
        } catch (error) {
          if (!active || logoController.signal.aborted || workspaceIdRef.current !== workspaceId) return
          const reason = error instanceof Error ? error.message : 'The Shopify logo could not be inspected.'
          setFacts(value => ({ ...value, logo: null })); setProvenanceOverrides(value => ({ ...value, logo: { tag: 'adjusted', reason: `${reason} Marva will use the shop-name wordmark instead.` } }))
        } finally { if (active && workspaceIdRef.current === workspaceId) setUploading(false) }
      })
      .catch(error => { if (active) setMessage(error instanceof Error ? error.message : 'Could not load current styling or a recent cart.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false; logoController.abort() }
  }, [currentWorkspace?.id])

  const accessibility = useMemo(() => deriveAccessibleBrandColor(facts.brandColor), [facts.brandColor]); const logoUrl = facts.logo?.url
  const hasUnsavedFacts = JSON.stringify(facts) !== JSON.stringify(savedFacts); const hasPendingSuggestion = Boolean(suggestion)
  useEffect(() => {
    if (!currentWorkspace?.id || !cart || !/^#[0-9a-f]{6}$/i.test(facts.brandColor)) return
    let active = true; const timer = window.setTimeout(() => workspacesApi.previewEmailStyle(currentWorkspace.id, facts, previewOffer, cart.cartId).then(response => { if (active) setPreviewHtml(response.success ? response.data?.htmlBody || null : null) }).catch(() => { if (active) setPreviewHtml(null) }), 250)
    return () => { active = false; window.clearTimeout(timer) }
  }, [cart, currentWorkspace?.id, facts, previewOffer])

  const choose = <K extends keyof BrandFacts>(key: K, value: BrandFacts[K]) => { setFacts(current => ({ ...current, [key]: value })); if (suggestion) setChosenFacts(current => new Set(current).add(key)) }
  const save = async () => {
    if (!workspaceLoaded) return setMessage('Reload this page before saving brand styling.')
    if (suggestion) return setMessage('Confirm the Shopify suggestion with “Looks right” before it becomes live.')
    if (!currentWorkspace || !/^#[0-9a-f]{6}$/i.test(facts.brandColor)) return setMessage('Brand colour must be a six-digit hex value such as #1D4ED8.')
    const workspaceId = currentWorkspace.id; setSaving(true); setMessage(null)
    try { const response = await workspacesApi.update(workspaceId, { emailStyles: facts } as never); if (workspaceIdRef.current !== workspaceId) return; if (!response.success) throw new Error(response.error || 'Failed to save brand styling'); if (response.data) setCurrentWorkspace(response.data as never); const saved = (response.data as { emailStyles?: BrandFacts } | undefined)?.emailStyles; if (saved) { setFacts(saved); setSavedFacts(saved); setHasStoredStyles(true) } setMessage('Brand styling saved successfully!') }
    catch (error) { if (workspaceIdRef.current === workspaceId) setMessage(error instanceof Error ? error.message : 'Failed to save brand styling') } finally { if (workspaceIdRef.current === workspaceId) setSaving(false) }
  }
  const confirmSuggestion = async () => {
    if (!currentWorkspace || !suggestion || !/^#[0-9a-f]{6}$/i.test(facts.brandColor)) return setMessage('Brand colour must be a six-digit hex value such as #1D4ED8.')
    const workspaceId = currentWorkspace.id; setSaving(true); setMessage(null)
    try { const response = await workspacesApi.confirmEmailBrandDetection(workspaceId, { suggestionId: suggestion.id, emailStyles: facts, ...(useShopifyName ? { useShopifyName: true } : {}) }); if (workspaceIdRef.current !== workspaceId) return; if (!response.success || !response.data) throw new Error(response.error || 'Could not confirm this Shopify suggestion.'); setCurrentWorkspace(response.data as never); const confirmed = (response.data as { emailStyles?: BrandFacts }).emailStyles || facts; setFacts(confirmed); setSavedFacts(confirmed); setHasStoredStyles(true); setSuggestion(null); setChosenFacts(new Set()); setProvenanceOverrides({}); onDetectionRef.current?.('current'); setMessage(response.warnings?.length ? 'Brand styling was saved. Marva could not finish cleaning up an older logo; no action is needed.' : 'Shopify brand styling confirmed and saved.') }
    catch (error) { if (workspaceIdRef.current === workspaceId) setMessage(error instanceof Error ? error.message : 'Could not confirm this Shopify suggestion.') } finally { if (workspaceIdRef.current === workspaceId) setSaving(false) }
  }
  const sendTest = async () => {
    const recipientEmail = user?.primaryEmailAddress?.emailAddress; if (!currentWorkspace || !cart || !recipientEmail) return setMessage('A recent abandoned cart and your account email are required for a test send.')
    const workspaceId = currentWorkspace.id; setSending(true); setMessage(null)
    try { const response = await governanceApi.sendTestEmail(workspaceId, { emailType: 'cart_recovery', recipientEmail, cartId: cart.cartId, previewOffer }); if (workspaceIdRef.current !== workspaceId) return; if (!response.success) throw new Error(response.error || 'Test send failed'); setMessage(`Test email sent to ${recipientEmail}.`) }
    catch (error) { if (workspaceIdRef.current === workspaceId) setMessage(error instanceof Error ? error.message : 'Test send failed') } finally { if (workspaceIdRef.current === workspaceId) setSending(false) }
  }
  const factValue = (key: keyof BrandFacts) => key === 'logo'
    ? (!facts.logo ? 'Shop-name wordmark' : facts.logo.url === savedFacts.logo?.url ? 'Current logo' : chosenFacts.has('logo') ? 'Chosen logo' : 'Shopify logo')
    : facts[key]
  const factProvenance = (key: keyof BrandFacts): EmailBrandFactProvenance | undefined => {
    if (chosenFacts.has(key)) return { tag: 'chosen', reason: 'You adjusted this before confirming.' }
    if (suggestion) return provenanceOverrides[key] || suggestion.provenance[key]
    // No suggestion in review: the live facts carry no stored provenance (detection metadata is
    // only on the review route), so only an edit made here is named — never a guessed source.
    return JSON.stringify(facts[key]) !== JSON.stringify(savedFacts[key]) ? { tag: 'chosen', reason: 'Not saved yet.' } : undefined
  }

  const onLogo = async (file: File) => {
    if (!currentWorkspace) return
    const workspaceId = currentWorkspace.id; setUploading(true)
    try { const raster = await rasteriseLogo(file); if (workspaceIdRef.current !== workspaceId) return; const response = await workspacesApi.uploadEmailLogo(workspaceId, { pngBase64: raster.data.split(',')[1], pixelWidth: raster.pixelWidth, pixelHeight: 80 }); if (workspaceIdRef.current !== workspaceId) return; const logo = (response.data as { logo?: BrandFacts['logo'] } | undefined)?.logo; if (!response.success || !logo) throw new Error(response.error || 'Could not stage logo'); choose('logo', logo); setMessage(`Logo ready to preview. ${suggestion ? 'Confirm the Shopify suggestion' : 'Save'} to use it in test sends.`) }
    catch (error) { if (workspaceIdRef.current === workspaceId) setMessage(error instanceof Error ? error.message : 'Could not read logo') }
    finally { if (workspaceIdRef.current === workspaceId) setUploading(false) }
  }

  /** With the Brand tab's nav, the preview is the aside; on its own, it follows the facts. */
  const frame = (main: ReactNode, aside: ReactNode) => nav
    ? <SectionLayout nav={nav} aside={aside} asideLabel="Preview" asideWidth="wide">{main}</SectionLayout>
    : <Stack>{main}{aside}</Stack>

  if (loading) return frame(<Card><LoadingState caption="Loading brand styling…" /></Card>, null)

  /** One fact as a row: its control (which names it), then where the value came from. */
  const factRow = (key: keyof BrandFacts, control: ReactNode) => {
    const provenance = factProvenance(key)
    return (
      <Stack gap="sm" key={key} data-testid={`fact-${key}`}>
        {control}
        {suggestion && <Hint>{FACT_LABELS[key]}: {factValue(key)}</Hint>}
        {provenance && <Hint><Badge tone={provenance.tag === 'chosen' ? 'gold' : 'neutral'}>{TAG_LABELS[provenance.tag]}</Badge> {provenance.reason}</Hint>}
      </Stack>
    )
  }

  const main = (
    <Section title="How we look" description="Five facts from your shop, for your emails — chat takes the colour and paper. Change what is wrong; Marva keeps the layout consistent.">
      <Stack>
        {message && <Alert tone="info" live="polite">{message}</Alert>}
        {!suggestion && detectionReason && <Alert tone="info">{detectionReason}</Alert>}

        <Card tone={suggestion ? 'gold' : 'plain'} data-testid={suggestion ? 'brand-detection-proposal' : undefined}>
          <CardHeader
            title={suggestion ? 'Marva found this in Shopify' : 'Five brand facts'}
            description={suggestion ? 'Review each fact. Your current styling stays live until you confirm.' : 'No presets and no layout controls.'}
            aside={suggestion ? <Chip tone="gold" dot>Awaiting your confirmation</Chip> : undefined}
          />
          <Stack>
            {factRow('logo', (
              <FileField
                label="Logo or shop-name wordmark"
                accept="image/png,image/jpeg,image/webp"
                value={logoUrl ? { src: logoUrl, name: factValue('logo') as string } : null}
                emptyText="Shop-name wordmark"
                busy={uploading}
                disabled={!workspaceLoaded}
                onSelect={file => void onLogo(file)}
                onRemove={() => choose('logo', null)}
                removeLabel="Use shop-name wordmark"
              />
            ))}
            {factRow('brandColor', (
              <ColorField label="One brand colour" disabled={!workspaceLoaded} value={facts.brandColor} onChange={hex => choose('brandColor', hex)}>
                {accessibility.darkened ? 'Darkened to pass accessibility' : 'Already passes accessibility'} — white text on {accessibility.original} is {accessibility.originalRatio.toFixed(2)}:1. Buttons use {accessibility.final} ({accessibility.finalRatio.toFixed(2)}:1).
              </ColorField>
            ))}
            {factRow('typeface', <Stack gap="sm"><FieldLabel>Typeface</FieldLabel><Seg label="Typeface" disabled={!workspaceLoaded} value={facts.typeface} values={['sans', 'serif'] as const} onChange={value => choose('typeface', value)} /></Stack>)}
            {factRow('corners', <Stack gap="sm"><FieldLabel>Corners</FieldLabel><Seg label="Corners" disabled={!workspaceLoaded} value={facts.corners} values={['square', 'soft', 'round'] as const} onChange={value => choose('corners', value)} /></Stack>)}
            {factRow('paper', <Stack gap="sm"><FieldLabel>Paper</FieldLabel><Seg label="Paper" disabled={!workspaceLoaded} value={facts.paper} values={['warm', 'white'] as const} onChange={value => choose('paper', value)} /></Stack>)}
            {suggestion && (
              <Stack gap="sm">
                {suggestion.logoCandidate && savedFacts.logo && facts.logo?.url !== savedFacts.logo.url && (
                  <div><Button variant="ghost" size="sm" disabled={uploading} onClick={() => choose('logo', savedFacts.logo)}>Keep current logo</Button></div>
                )}
                {suggestion.observedShopName && suggestion.observedShopName !== currentWorkspace?.name && (
                  <Checkbox
                    checked={useShopifyName}
                    onChange={event => setUseShopifyName(event.target.checked)}
                    label={<>Use “{suggestion.observedShopName}” as the shop name too</>}
                  />
                )}
                <div>
                  <Button size="sm" onClick={confirmSuggestion} busy={saving} busyLabel="Confirming…" disabled={uploading || !workspaceLoaded}>
                    {uploading ? 'Checking Shopify logo…' : 'Looks right'}
                  </Button>
                </div>
              </Stack>
            )}
          </Stack>
        </Card>

        <Card>
          <CardHeader title="Kept consistent by Marva" description="These decisions are locked, with their reason." />
          <KeyValue items={LOCKED.map(([name, reason]) => ({ label: name, value: reason }))} />
        </Card>

        {(hasUnsavedFacts || hasPendingSuggestion) && <Alert tone="attention">{hasPendingSuggestion ? 'Confirm this suggestion before sending a test so the live email uses exactly this preview.' : 'Save these facts before sending a test so the Phase 1 email uses exactly this preview.'}</Alert>}
        <Inline justify="between">
          <Button variant="ghost" size="sm" onClick={sendTest} busy={sending} busyLabel="Sending…" disabled={!workspaceLoaded || uploading || hasPendingSuggestion || hasUnsavedFacts || !cart || !cart.items.some(item => item.productId)}>
            Send me a test
          </Button>
          <Inline justify="end">
            {/* Cancel puts the facts back to what is live — never a no-op button. In review the
                way out is the suggestion itself, so it is not offered there. */}
            {!suggestion && <Button variant="ghost" size="sm" disabled={!hasUnsavedFacts || saving || uploading} onClick={() => { setFacts(savedFacts); onClose?.() }}>Cancel</Button>}
            <Button size="sm" onClick={save} busy={saving} busyLabel="Saving…" disabled={!workspaceLoaded || uploading || hasPendingSuggestion}>
              {uploading ? 'Preparing logo…' : 'Save brand styling'}
            </Button>
          </Inline>
        </Inline>
      </Stack>
    </Section>
  )

  // The draft as the widget would read it: with nothing stored and nothing changed, the widget
  // keeps its own look — these defaults are the form's starting point, not what chat paints.
  const chatFacts = hasStoredStyles || hasUnsavedFacts || hasPendingSuggestion ? facts : undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assistantName = (((currentWorkspace as any)?.assistantName ?? '') as string)
  const aside = (
    <Stack gap="sm">
      <Inline justify="between">
        <Eyebrow tone="muted">{surface === 'chat' ? 'Preview · chat' : 'Preview · real abandoned cart'}</Eyebrow>
        <PreviewSurfaceControl value={surface} onChange={setSurface} />
      </Inline>
      {surface === 'chat' ? <ChatPreview facts={chatFacts} assistantName={assistantName} /> : (
        <>
          <Seg label="Preview offer" value={previewOffer ? 'A 10% offer' : 'No offer'} values={['No offer', 'A 10% offer'] as const} onChange={value => setPreviewOffer(value === 'A 10% offer')} />
          {/* D-42: the merchant-branded email is an external surface — the shared renderer draws
              it inside this frame; only the frame is the app's. */}
          {cart && previewHtml
            ? <EmailPreview html={previewHtml} height={620} compactHeight={520} />
            : <Alert tone="info">No abandoned cart is available yet. Marva will use the shop-name wordmark and these five facts when one is.</Alert>}
        </>
      )}
    </Stack>
  )

  return frame(main, aside)
}

export default EmailStylesSection
